import { ChangeDetectionStrategy, Component, computed, effect, inject, linkedSignal, untracked } from "@angular/core";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { FormsModule } from "@angular/forms";
import { S25SidebarComponent } from "../../../s25-sidebar/s25.sidebar.component";
import { SidebarStore } from "../../../s25-sidebar/s25.sidebar.store";
import { S25DatepickerModule } from "../../../s25-datepicker/s25.datepicker.module";
import { S25LoadingInlineStaticComponent } from "../../../s25-loading/s25.loading.inline.static.component";
import { S25PaymentAmountFieldComponent } from "./s25.payment.amount.field.component";
import { PaymentDetails, PaymentEmailPayload, S25PaymentEmailComponent } from "./s25.payment.email.component";
import { PaymentSource, PaymentSourceId, PaymentsStore } from "./s25.payments.store";
import { PaymentRows } from "./payment-rows";
import { PaymentEmailBody } from "./payment-email-body";
import { PaymentEmailSend } from "./payment-email-send";
import { PaymentEmailTemplate } from "./payment-email-template";
import { IPaymentDetails } from "../../s25-payments/payment.service";
import { IntegrationService, IntegrationTypes } from "../../../integrations/integration.service";
import { EmailClass } from "../../../../services/email.service";
import { ContactRole } from "../../../../services/organization.service";
import { BpeService } from "../../../bpe/bpe.service";
import { S25Datefilter } from "../../../s25-dateformat/s25.datefilter.service";
import { fromContactRole } from "../../../s25-email/s25.email.recipients.component";
import { FormAssignmentRecipient } from "../../../data-ingestion/data.ingestion.service";
import { S25PricingUtil } from "../../s25.pricing.util";
import { Report } from "../../../../pojo/Report";
import { DocumentService } from "../../../../services/document.service";
import { S25Util } from "../../../../util/s25-util";
import { s25Resource } from "../../../../util/s25.resource";
import { Bind } from "../../../../decorators/bind.decorator";

const TYPE_OPTIONS: IPaymentDetails["type"][] = ["DEPOSIT", "FINAL", "MISC"];

const BILLING_ROLE_ID = -1;

@TypeManagerDecorator("s25-ng-payment-request")
@Component({
    selector: "s25-ng-payment-request",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        S25SidebarComponent,
        S25PaymentEmailComponent,
        S25DatepickerModule,
        S25LoadingInlineStaticComponent,
        S25PaymentAmountFieldComponent,
    ],
    providers: [SidebarStore],
    template: `
        <s25-ng-sidebar [title]="title()" [mode]="'overlay'" [width]="640" (closed)="close()">
            @if (sidebarStore.isOpen()) {
                <!-- Whatever the opener needs above the form: the Invoices tab puts its invoice
                     picker here, since only it knows the organization's other events. -->
                <ng-content />

                @if (paymentsStore.requestLoading()) {
                    <s25-ng-loading-inline-static [text]="'Loading invoice…'" />
                } @else if (orgId()) {
                    @if (emailOnly()) {
                        <p class="request-summary">
                            Writing to <strong>{{ orgName() }}</strong> about an outstanding balance of
                            <strong>{{ balanceLabel() }}</strong
                            >. No payment is requested or recorded.
                        </p>
                    } @else {
                        <p class="request-summary">
                            Requesting payment from <strong>{{ orgName() }}</strong> against an outstanding balance of
                            <strong>{{ balanceLabel() }}</strong
                            >.
                        </p>

                        <div class="request-fields">
                            <label class="request-field">
                                <span>Payment type</span>
                                <select
                                    class="cn-form__control"
                                    [ngModel]="type()"
                                    (ngModelChange)="type.set($event)"
                                    [disabled]="isReminder()"
                                >
                                    @for (option of typeOptions; track option) {
                                        <option [value]="option">{{ typeLabel(option) }}</option>
                                    }
                                </select>
                                @if (duplicateTypeWarning(); as warning) {
                                    <span class="request-error" role="alert">{{ warning }}</span>
                                }
                            </label>

                            <!-- Always shown, so a server without the integration endpoints still says
                             what the request will be collected through. -->
                            <label class="request-field">
                                <span>Payment processor <span aria-hidden="true">*</span></span>
                                <select
                                    class="cn-form__control"
                                    [ngModel]="sourceId()"
                                    (ngModelChange)="sourceId.set($event)"
                                    [disabled]="isReminder()"
                                >
                                    <option [ngValue]="undefined">Select Processor</option>
                                    @for (source of processorOptions(); track source.id) {
                                        <option [ngValue]="source.id">{{ source.name }}</option>
                                    }
                                </select>
                            </label>

                            <div class="request-field request-field--newline">
                                <span>Amount</span>
                                <s25-ng-payment-amount-field
                                    [(entry)]="amount"
                                    [balance]="balance()"
                                    [orientation]="'inline'"
                                    [groupName]="'payment-request-amount'"
                                    [disabled]="isReminder()"
                                />
                            </div>

                            <div class="request-field">
                                <span>Due date</span>
                                <s25-datepicker
                                    [value]="dueDate()"
                                    (valueChange)="setDueDate($event)"
                                    [ariaLabel]="'Due date'"
                                    [disabled]="isReminder()"
                                />
                            </div>
                        </div>
                    }

                    @if (contacts.isLoading() || paymentsStore.requestDefaultsLoading()) {
                        <s25-ng-loading-inline-static />
                    } @else {
                        <s25-ng-payment-email
                            [orgMembers]="orgMembers()"
                            [defaultToRecipients]="defaultToRecipients()"
                            [defaultCcRecipients]="defaultCcRecipients()"
                            [defaultBccRecipients]="defaultBccRecipients()"
                            [defaultFrom]="defaultFrom()"
                            [defaultSubject]="defaultSubject()"
                            [defaultBody]="defaultBody()"
                            [reports]="offeredReports()"
                            [defaultReportIds]="defaultReportIds()"
                            [templates]="paymentsStore.templates()"
                            [profiles]="paymentsStore.profiles()"
                            [paymentDetails]="paymentDetails()"
                            [templateLoading]="templateResolving()"
                            [(templateId)]="templateId"
                            [(profileId)]="profileId"
                            [seedKey]="seedKey()"
                            [sendHandler]="sendPaymentRequest"
                            (closed)="close()"
                        />
                    }
                }
            }
        </s25-ng-sidebar>
    `,
    styles: `
        :host {
            display: block;
        }

        .request-summary {
            margin-bottom: 0.75rem;
        }

        .request-fields {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 0.75rem 1rem;
            margin-bottom: 1.25rem;
        }

        .request-field--newline {
            grid-column: 1;
        }

        @media (max-width: 600px) {
            .request-fields {
                grid-template-columns: minmax(0, 1fr);
            }
        }

        .request-field {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
            font-weight: 600;
        }

        .request-field .cn-form__control {
            font-weight: 400;
        }

        .request-error {
            font-weight: 400;
            color: var(--color--text-danger);
        }

        :host ::ng-deep .sidebar .sidebar-content {
            padding-top: 0 !important;
        }
    `,
})
export class S25PaymentRequestComponent {
    protected readonly sidebarStore = inject(SidebarStore);
    protected readonly paymentsStore = inject(PaymentsStore);

    protected readonly typeOptions = TYPE_OPTIONS;

    protected readonly orgId = this.paymentsStore.requestOrgId;
    protected readonly sources = this.paymentsStore.sources;

    /** Set when the sidebar was opened from a row, meaning the payment already exists. */
    protected readonly reminderPayment = this.paymentsStore.reminderPayment;
    protected readonly emailOnly = this.paymentsStore.emailOnly;

    protected readonly isReminder = computed(() => !!this.paymentsStore.requestPaymentId());
    protected readonly title = computed(() => {
        if (this.emailOnly()) return "Email Billing Contact";
        return this.isReminder() ? "Send Payment Reminder" : "Send Payment Request";
    });

    /**
     * Identifies one opening of the form. Keyed on the payment and the contact as well as the org,
     * so that chasing two payments of the same org in a row, or writing to two of its contacts,
     * re-seeds the fields and the message the second time.
     */
    protected readonly requestKey = computed(
        () =>
            `${this.orgId() ?? ""}:${this.paymentsStore.requestPaymentId() ?? ""}:` +
            `${this.paymentsStore.requestContactId() ?? ""}`,
    );

    protected readonly orgName = computed(() =>
        this.orgId() ? (this.paymentsStore.orgNameById().get(this.orgId()!) ?? "this organization") : "",
    );
    protected readonly balance = computed(() =>
        this.orgId() ? this.paymentsStore.remainingBalance(this.orgId()!) : 0,
    );
    protected readonly balanceLabel = computed(() => S25PricingUtil.formatCurrency(this.balance()));

    // Every field restarts from the form opening that it belongs to, so a request never carries
    // over an amount or a due date from the organization above it. A reminder is not composed at
    // all - it reports what was already logged.
    protected readonly type = linkedSignal<string, IPaymentDetails["type"]>({
        source: this.requestKey,
        computation: () => untracked(() => this.reminderPayment()?.type ?? "DEPOSIT"),
    });
    protected readonly dueDate = linkedSignal<string, Date>({
        source: this.requestKey,
        computation: () =>
            untracked(() => {
                const dueDate = this.reminderPayment()?.dueDate;
                return dueDate ? new Date(dueDate) : new Date();
            }),
    });
    protected readonly amount = linkedSignal<string, PaymentRows.PaymentAmountEntry>({
        source: this.requestKey,
        computation: () =>
            untracked(() => {
                const reminder = this.reminderPayment();
                if (reminder) {
                    return { value: S25PricingUtil.convertToDollars(reminder.amountTotalCents), type: "amount" };
                }
                return { value: this.balance(), type: "amount" };
            }),
    });

    /**
     * A reminder can only be for the processor its payment was made through, and that processor may
     * since have been disabled, so it is named from the payment rather than looked up.
     */
    protected readonly processorOptions = computed<PaymentSource[]>(() => {
        const reminder = this.reminderPayment();
        if (!reminder) return this.sources();
        return [{ id: reminder.source as PaymentSourceId, name: PaymentRows.paymentSourceLabel(reminder.source) }];
    });

    // With one processor there is nothing to choose, so it is chosen for them.
    protected readonly sourceId = linkedSignal<PaymentSource[], PaymentSourceId | undefined>({
        source: this.processorOptions,
        computation: (options) => (options.length === 1 ? options[0]?.id : undefined),
    });

    // Zero means no template, so the built-in defaults stand. Both reset with the form, so a
    // template picked for one organization is not still applied to the next.
    protected readonly templateId = linkedSignal<string, number>({
        source: this.requestKey,
        computation: () => 0,
    });
    protected readonly profileId = linkedSignal<string, number>({
        source: this.requestKey,
        computation: () => PaymentEmailTemplate.ALL_PROFILES_ID,
    });

    private readonly pickedTemplate = computed(() =>
        this.paymentsStore.templates().find((template) => template.itemId === this.templateId()),
    );

    /**
     * Interpolating a template needs the whole event, billing included, so it is only fetched once a
     * template is actually picked.
     */
    protected readonly templateEventData = s25Resource({
        if: () => !!this.pickedTemplate() && !!this.paymentsStore.eventId(),
        params: () => this.paymentsStore.eventId()!,
        loader: async ({ params: eventId }) => {
            const eventData = await BpeService.getEventData(eventId, { cache: true, longCache: true });
            if (eventData) {
                await BpeService.getAdditionalEventData({ postEventDataPromise: Promise.resolve(eventData) });
            }
            return eventData;
        },
    });

    /** The message the picked template evaluates to for the chosen segment. */
    protected readonly templateEmail = s25Resource({
        if: () => !!this.pickedTemplate() && !!this.templateEventData.value(),
        params: () => ({
            template: this.pickedTemplate()!,
            profileId: this.profileId(),
            eventData: this.templateEventData.value()!,
        }),
        loader: ({ params }): Promise<PaymentEmailTemplate.TemplateEmail | null> =>
            BpeService.getEmailObjectFromTemplate(
                params.eventData,
                PaymentEmailTemplate.templateProfileId(params.profileId),
                params.template,
            ),
    });

    /** Only once a template has actually resolved does it get to speak for the form. */
    private readonly template = computed(() =>
        this.templateId() ? (this.templateEmail.value() ?? undefined) : undefined,
    );

    /** Report ids the template names that are not regular reports - candidates for a document. */
    private readonly unmatchedReportIds = computed(() =>
        PaymentEmailTemplate.unmatchedReportIds(this.template()?.reports, this.paymentsStore.reports()),
    );

    // Documents are only ever attached through a template, so they are only fetched for one that
    // names a report id the event's regular reports do not answer to.
    protected readonly eventDocuments = s25Resource({
        if: () => this.unmatchedReportIds().length > 0,
        loader: () => DocumentService.getDocumentList({ type: "event" }),
    });
    protected readonly organizationDocuments = s25Resource({
        if: () => this.unmatchedReportIds().length > 0,
        loader: () => DocumentService.getDocumentList({ type: "organization" }),
    });

    private readonly templateDocuments = computed<Report.SimpleObject[]>(() => {
        const unmatched = new Set(this.unmatchedReportIds());
        if (!unmatched.size) return [];
        return PaymentEmailTemplate.documentReports(
            this.paymentsStore.eventReports(),
            this.eventDocuments.value() ?? [],
            this.organizationDocuments.value() ?? [],
        ).filter((document) => unmatched.has(Number(document.rpt_id)));
    });

    /** Everything a picked template needs before it can seed the form: its message and its documents. */
    protected readonly templateResolving = computed(
        () =>
            this.templateEventData.isLoading() ||
            this.templateEmail.isLoading() ||
            (this.unmatchedReportIds().length > 0 &&
                (this.eventDocuments.isLoading() || this.organizationDocuments.isLoading())),
    );

    /** A picked template resolves after the pick, so the form has to be seeded again once it lands. */
    private readonly templateSeedState = computed(() => {
        if (!this.templateId()) return "none";
        return this.templateResolving() ? "loading" : "ready";
    });

    /**
     * Re-seeds the compose form. Keyed on the template and segment as well as the opening, so
     * picking a template overwrites the fields instead of leaving an earlier draft in place.
     */
    protected readonly seedKey = computed(
        () => `${this.requestKey()}:${this.templateId()}:${this.profileId()}:${this.templateSeedState()}`,
    );

    protected readonly defaultFrom = computed(() => this.template()?.from || this.paymentsStore.defaultFromAddress());
    protected readonly defaultSubject = computed(() => {
        const subject = this.template()?.subject;
        if (subject) return S25Util.unescapeXml(subject);

        return this.emailOnly()
            ? PaymentEmailBody.defaultContactEmailSubject(this.orgName())
            : PaymentEmailBody.defaultPaymentEmailSubject(this.paymentsStore.eventName());
    });
    /** What the payer has to be told for this processor, and how the checkbox describes it. */
    protected readonly paymentDetails = computed<PaymentDetails | undefined>(() => {
        // Nothing is being collected, so there is no processor to explain and no link to offer.
        if (this.emailOnly()) return undefined;

        const body = PaymentEmailBody.paymentDetailsBlock(this.sourceId(), this.orgName());
        if (!body) return undefined;

        if (this.sourceId() !== IntegrationTypes.sevenPoint) return { label: "Include the payment link", body };

        return {
            label: "Include the payment link and identifier",
            body,
            withoutLink: {
                label: "Include the payment identifier",
                body: PaymentEmailBody.paymentDetailsBlock(this.sourceId(), this.orgName(), false),
            },
        };
    });

    /**
     * A template's message says nothing about the processor, so the block is seeded onto the end
     * where the author can see it and untick it. The default body already carries its own.
     */
    protected readonly defaultBody = computed(() => {
        const template = this.template();
        if (!template) {
            if (this.emailOnly()) {
                return PaymentEmailBody.defaultContactEmailBody({
                    balance: this.balance(),
                    orgName: this.orgName(),
                    eventName: this.paymentsStore.eventName(),
                });
            }

            return PaymentEmailBody.defaultPaymentEmailBody({
                amount: PaymentRows.paymentAmountDollars(this.amount(), this.balance()),
                orgName: this.orgName(),
                eventName: this.paymentsStore.eventName(),
                sourceId: this.sourceId(),
            });
        }

        const body = template.body ?? "";
        if (this.emailOnly()) return body;

        const templateBody = PaymentEmailBody.hasPaymentPortal(this.sourceId())
            ? body
            : PaymentEmailBody.removePaymentLink(body);
        const includeLink = !PaymentEmailBody.usesPaymentLink(templateBody);
        return templateBody + PaymentEmailBody.paymentDetailsBlock(this.sourceId(), this.orgName(), includeLink);
    });

    /** Documents are only offered when the applied template names them. */
    protected readonly offeredReports = computed(() => {
        const reports = this.paymentsStore.reports();
        return this.template() ? [...reports, ...this.templateDocuments()] : reports;
    });

    /** Without a template the invoice is attached; with one, the template says what rides along. */
    protected readonly defaultReportIds = computed(() => {
        const template = this.template();
        if (!template) return this.paymentsStore.defaultReportIds();
        return PaymentEmailTemplate.templateReportIds(template.reports, this.offeredReports());
    });

    protected readonly defaultToRecipients = computed<FormAssignmentRecipient[]>(() => {
        const template = this.template();
        if (template) return PaymentEmailTemplate.templateRecipients(template.to, this.paymentsStore.roleRecipients());
        const addressed = this.addressedContact();
        return (addressed ? [addressed] : this.billingContacts()).map(fromContactRole);
    });

    private readonly addressedContact = computed(() => {
        const contactId = this.paymentsStore.requestContactId();
        if (!contactId) return undefined;
        return this.orgMembers().find((contact) => contact.itemId === contactId);
    });
    protected readonly defaultCcRecipients = computed(() =>
        PaymentEmailTemplate.templateRecipients(this.template()?.cc, this.paymentsStore.roleRecipients()),
    );
    protected readonly defaultBccRecipients = computed(() =>
        PaymentEmailTemplate.templateRecipients(this.template()?.bcc, this.paymentsStore.roleRecipients()),
    );

    /**
     * Only one balance payment may exist, so asking for a second one is refused up front. A reminder
     * is that one payment, so it is never in its own way.
     */
    protected readonly duplicateTypeWarning = computed(() => {
        const orgId = this.orgId();
        if (!orgId || this.isReminder() || this.type() !== "FINAL") return "";
        if (!this.paymentsStore.existingPaymentsOfType(orgId, "FINAL").length) return "";
        return "A BALANCE payment already exists. Send a reminder for it instead.";
    });

    protected readonly contacts = s25Resource({
        if: () => !!this.orgId(),
        params: () => this.orgId()!,
        loader: ({ params: orgId }) => this.paymentsStore.organizationContacts(orgId),
    });

    protected readonly orgMembers = computed<ContactRole[]>(() => this.contacts.value() ?? []);
    protected readonly billingContacts = computed(() =>
        this.orgMembers().filter((contact) => contact.role?.itemId === BILLING_ROLE_ID),
    );

    constructor() {
        effect(() => {
            const wantsOpen = !!this.orgId();
            if (wantsOpen !== this.sidebarStore.isOpen()) this.sidebarStore.toggle();
        });
    }

    protected typeLabel(type: IPaymentDetails["type"]) {
        return PaymentRows.paymentTypeLabel(type);
    }

    protected setDueDate(value: number | string | Date | null | undefined) {
        if (value) this.dueDate.set(new Date(value));
    }

    protected close() {
        this.paymentsStore.closeRequest();
    }

    @Bind
    protected async sendPaymentRequest(email: PaymentEmailPayload): Promise<{ error?: string }> {
        const orgId = this.orgId();
        if (!orgId) return { error: "No organization is selected." };

        const body = this.emailOnly() ? this.contactEmailBody(email) : await this.paymentRequestBody(email, orgId);
        if ("error" in body) return { error: body.error };

        const message: EmailClass = {
            subject: email.subject,
            body: body.body,
            to: email.to,
            cc: email.cc,
            bcc: email.bcc,
            from: email.from,
            reports: [],
        };

        const [, sendError] = await S25Util.Maybe(
            PaymentEmailSend.sendPaymentEmail(
                {
                    eventId: this.paymentsStore.eventId()!,
                    evBillId: this.paymentsStore.evBillId()!,
                    orgId,
                    templateId: email.templateId,
                },
                message,
                email.reports,
            ),
        );
        if (sendError) return { error: S25Util.errorText(sendError, "The email could not be sent.") };

        return {};
    }

    /**
     * Logs the payment being asked for, unless it is a reminder for one already on the books, and
     * resolves the message against it.
     */
    private async paymentRequestBody(
        email: PaymentEmailPayload,
        orgId: number,
    ): Promise<{ body: string } | { error: string }> {
        const duplicate = this.duplicateTypeWarning();
        if (duplicate) return { error: duplicate };

        const sourceId = this.sourceId();
        if (!sourceId) return { error: "A payment processor must be selected." };

        const dollars = PaymentRows.paymentAmountDollars(this.amount(), this.balance());
        if (dollars <= 0) return { error: "Enter an amount greater than $0." };

        const reminder = this.reminderPayment();
        let paymentDetailId = reminder?.paymentDetailId;

        if (!reminder) {
            const { payment, error } = await this.paymentsStore.createPayment({
                productName: this.paymentsStore.eventName(),
                productDescription: email.subject,
                amountInCents: S25PricingUtil.convertToCents(dollars),
                organizationId: orgId,
                type: this.type(),
                source: sourceId,
                dueDate: S25Util.date.toS25ISODateStrStartOfDay(this.dueDate()),
                paymentStatus: "unpaid",
                ...checkoutUrls(sourceId),
            });

            if (error) return { error };
            paymentDetailId = payment?.paymentDetailId;
        }

        if (!paymentDetailId) return { error: "Error creating payment details." };

        const paymentUrl = await paymentPortalUrl(sourceId, paymentDetailId);

        return {
            body: PaymentEmailBody.resolvePaymentTokens(email.body, {
                paymentUrl,
                paymentDetailId,
                amount: S25PricingUtil.formatCurrency(dollars),
                dueDate: S25Datefilter.transform(this.dueDate(), this.paymentsStore.dateFormat() ?? "") ?? "",
                type: PaymentRows.paymentTypeLabel(this.type()),
                // A template's message is governed by the compose form's checkbox, so only the
                // default body gets a link it no longer names.
                appendMissingLink: !email.templateId,
            }),
        };
    }

    private contactEmailBody(email: PaymentEmailPayload): { body: string } {
        return {
            body: PaymentEmailBody.resolvePaymentTokens(email.body, {
                paymentUrl: "",
                paymentDetailId: undefined,
                amount: S25PricingUtil.formatCurrency(this.balance()),
                dueDate: "",
                type: "",
                appendMissingLink: false,
            }),
        };
    }
}

/** Stripe returns the payer to 25Live, so it needs somewhere to send them on the way back. */
function checkoutUrls(sourceId: string): { successUrl: string; cancelUrl: string } {
    if (sourceId !== IntegrationTypes.stripe) return { successUrl: "", cancelUrl: "" };

    const base = window.location.origin + window.location.pathname;
    return {
        successUrl: `${base}#!/home/payment/success/{CHECKOUT_SESSION_ID}`,
        cancelUrl: `${base}#!/home/payment/cancel`,
    };
}

/** Where the payer goes to settle up. A manual payment has no portal, so it has no link. */
async function paymentPortalUrl(sourceId: string, paymentDetailId: number): Promise<string> {
    if (sourceId === IntegrationTypes.stripe) {
        return `${window.location.origin}${window.location.pathname}#!/payment/portal/${paymentDetailId}`;
    }
    if (sourceId === IntegrationTypes.sevenPoint) {
        const integration = await IntegrationService.getIntegration(IntegrationTypes.sevenPoint);
        return integration?.url ?? "";
    }
    return "";
}
